/**
 * The keyboard's copy, cut and paste (Ctrl+C, Ctrl+X, Ctrl+V) on the editor,
 * through the browser's own clipboard events rather than keydown: an event's
 * clipboard data is readable and writable without the permission prompt the
 * async clipboard API shows on read. In a text field the browser keeps them.
 */
import type { EntityId } from "../../world/entity";
import type { ComponentRegistry } from "../../world/schema/ComponentRegistry";
import type { WorldSnapshot } from "../../world/World";
import { copiedText, cutEdit, parseClipboard, pasteEdit } from "../create/entityCopies";
import type { SelectionState } from "../selection/Selection";
import type { Edit } from "../selection/selectionEdits";
import type { KeyContext } from "../shell/shortcuts";

/** The parts of a DOM ClipboardEvent this reads and writes. */
export interface ClipboardEventLike {
  readonly type: string;
  readonly clipboardData: {
    getData(format: string): string;
    setData(format: string, data: string): void;
  } | null;
  preventDefault(): void;
}

export interface ClipboardTarget {
  readonly world: Pick<WorldSnapshot, "entities" | "roots">;
  readonly registry: ComponentRegistry;
  readonly selection: SelectionState;
  /** Runs an edit through the Command Bus. */
  run(edit: Edit): void;
  /** Keeps the copied text in the editor too (EntityClipboard.hold). */
  hold(text: string): void;
  /** The editor's own last copy (EntityClipboard.kept), or null. */
  kept(): string | null;
}

/** Handles a copy, cut or paste event on the editor; leaves anything else to the browser. */
export function onClipboardEvent(
  event: ClipboardEventLike,
  focus: KeyContext["focus"],
  target: ClipboardTarget,
): void {
  if (focus !== "none") return;

  if (event.type === "copy" || event.type === "cut") {
    const copied = copySelection(event, target);
    if (copied && event.type === "cut") {
      const cut = cutEdit(
        target.world.entities,
        target.selection,
        target.selection.ids,
        "keyboard",
      );
      if (cut) target.run(cut);
    }
    return;
  }

  if (event.type === "paste") {
    // An event with no text at all (WebKit can withhold even this page's own
    // copy) gets the editor's last copy; other text is the visitor's newer copy.
    const eventText = event.clipboardData?.getData("text/plain") ?? "";
    const text = eventText !== "" ? eventText : target.kept();
    const copies = parseClipboard(text ?? "");
    if (!copies) return;
    event.preventDefault();
    const active: EntityId | null = target.selection.active;
    const paste = pasteEdit(copies, target.world, target.registry, active, "keyboard");
    if (paste) target.run(paste);
  }
}

/** Puts the selection on the event's clipboard; false when nothing can be copied. */
function copySelection(event: ClipboardEventLike, target: ClipboardTarget): boolean {
  const text = copiedText(target.world.entities, target.registry, target.selection.ids);
  if (text === null) return false;

  event.clipboardData?.setData("text/plain", text);
  target.hold(text);
  event.preventDefault();
  return true;
}
